import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Battery, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";


const stateColors = {
  Neuve: "border-neon-purple/30 text-neon-purple bg-neon-purple-muted",
  "Tres bon": "border-neon-cyan/30 text-neon-cyan bg-neon-cyan-muted",
  Bon: "border-neon-green/30 text-neon-green bg-neon-green-muted",
  "A surveiller": "border-neon-yellow/30 text-neon-yellow bg-neon-yellow-muted",
  Defectueuse: "border-neon-red/30 text-neon-red bg-neon-red-muted",
};

export default function CellHistoryPage() {
  const { cellId } = useParams();
  const navigate = useNavigate();
  const [cell, setCell] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cellRes, measRes] = await Promise.all([
          api.get(`/api/cells/${cellId}`),
          api.get(`/api/measurements/by-cell/${cellId}`),
        ]);
        setCell(cellRes.data);
        setMeasurements((measRes.data.measurements || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, [cellId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-neon-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!cell) {
    return (
      <div className="text-center py-20 text-muted-foreground font-mono">
        Cellule non trouvee
        <br />
        <Button variant="outline" className="mt-4 border-neon-blue/20 bg-transparent rounded-lg" onClick={() => navigate("/library")}>Retour a la bibliotheque</Button>
      </div>
    );
  }

  const chartData = measurements.map((m) => ({
    time: new Date(m.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    voltage: m.voltage,
  }));

  return (
    <div className="space-y-6" data-testid="cell-history-page">
      <Button variant="outline" onClick={() => navigate("/library")} className="border-neon-blue/20 hover:border-neon-blue hover:text-neon-blue rounded-lg bg-transparent" data-testid="back-to-library">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour a la bibliotheque
      </Button>

      {/* Cell Info */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="cell-info-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-neon-blue/20 to-neon-purple/20 border border-neon-blue/30 rounded-xl flex items-center justify-center neon-glow-blue">
              <Battery className="w-6 h-6 text-neon-blue" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-bold text-2xl text-neon-blue neon-text-blue" data-testid="cell-detail-number">{cell.cell_number}</h2>
              <div className="flex flex-wrap gap-4 mt-2">
                <div><span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">Serie</span><span className="font-mono text-sm">{cell.series}</span></div>
                <div><span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">Capacite</span><span className="font-mono text-sm">{cell.capacity_ah ?? "—"} Ah</span></div>
                <div><span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">V Debut</span><span className="font-mono text-sm">{cell.voltage_start ?? "—"} V</span></div>
                <div><span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">V Fin</span><span className="font-mono text-sm">{cell.voltage_end ?? "—"} V</span></div>
                <div><span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">Resistance</span><span className="font-mono text-sm">{cell.internal_resistance ?? "—"} mohm</span></div>
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">Etat</span>
                  <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-wider ${stateColors[cell.state] || ""}`}>{cell.state}</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Voltage Chart */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="history-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Activity className="w-4 h-4 text-neon-blue" />
            Historique des tensions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(59, 130, 246, 0.08)" />
                <XAxis dataKey="time" tick={{ fill: "#4B5E7A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(59, 130, 246, 0.15)" }} tickLine={false} />
                <YAxis domain={["auto", "auto"]} tick={{ fill: "#4B5E7A", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "rgba(59, 130, 246, 0.15)" }} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "#111d32", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", fontFamily: "JetBrains Mono", fontSize: "11px" }} />
                <Line type="monotone" dataKey="voltage" stroke="#3B82F6" strokeWidth={2} dot={{ fill: "#3B82F6", r: 3, stroke: "rgba(59, 130, 246, 0.3)", strokeWidth: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-muted-foreground font-mono text-sm">
              Aucune mesure enregistree
            </div>
          )}
        </CardContent>
      </Card>

      {/* Measurements Table */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="history-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base text-white">Historique des mesures ({measurements.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-neon-blue/10 hover:bg-transparent">
                  <TableHead className="bg-cyber-elevated text-xs font-mono uppercase tracking-wider text-muted-foreground">Date</TableHead>
                  <TableHead className="bg-cyber-elevated text-xs font-mono uppercase tracking-wider text-muted-foreground">Heure</TableHead>
                  <TableHead className="bg-cyber-elevated text-xs font-mono uppercase tracking-wider text-muted-foreground">Tension (V)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {measurements.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground font-mono text-sm">Aucune mesure</TableCell></TableRow>
                ) : (
                  measurements.map((m) => (
                    <TableRow key={m.id} className="border-neon-blue/5 hover:bg-cyber-highlight transition-colors">
                      <TableCell className="font-mono text-sm">{new Date(m.timestamp).toLocaleDateString("fr-FR")}</TableCell>
                      <TableCell className="font-mono text-sm">{new Date(m.timestamp).toLocaleTimeString("fr-FR")}</TableCell>
                      <TableCell className={`font-mono text-sm font-bold ${m.voltage < 2.5 ? "text-neon-red" : "text-neon-blue neon-text-blue"}`}>{m.voltage} V</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
